'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowsPointingOutIcon,
  EllipsisVerticalIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';

type PlayOverlayProps = {
  videoId: string;
  autoPlay?: boolean;
  muted?: boolean;
};

/**
 * @description Controls for video player (play, pause, volume, fullscreen, etc.)
 * @param videoId - The ID of the video element (required)
 * @param autoPlay - Whether to auto play the video
 * @returns The play overlay component
 */

export const VideoPlayerOverlay = ({
  videoId,
  autoPlay = false,
  muted = false,
}: PlayOverlayProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [volume, setVolume] = useState(1);
  const [prevVolume, setPrevVolume] = useState(1);
  const [error, setError] = useState<MediaError | null>(null);

  // Scrolls to the current time of the video
  const handleTimeUpdate = useCallback(() => {
    if (!isDragging && videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [isDragging]);

  // Sets the duration of the video
  const handleDurationChange = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  }, []);

  // Sync with video element and handle autoPlay
  useEffect(() => {
    const video = document.getElementById(videoId) as HTMLVideoElement | null;
    if (video) {
      if (video.error) {
        console.error('Video error:', video.error);
        setError(video.error);
        return;
      }
      videoRef.current = video;
      setDuration(video.duration);

      // Set initial muted state and volume
      video.muted = muted;
      if (muted) {
        setVolume(0);
        setPrevVolume(1);
      } else {
        setVolume(video.volume);
      }

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleFullscreenChange = () => {
        if (!document.fullscreenElement && !video.paused) {
          video.pause();
          setIsPlaying(false);
        }
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('durationchange', handleDurationChange);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      document.addEventListener('fullscreenchange', handleFullscreenChange);

      if (autoPlay) {
        video.play().catch(() => {
          setIsPlaying(false);
        });
      }
      setIsPlaying(!video.paused);
      setIsReady(true);
      setCurrentTime(video.currentTime);

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('durationchange', handleDurationChange);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange
        );
      };
    } else {
      setIsReady(false);
      setIsPlaying(false);
    }
  }, [videoId, autoPlay, handleTimeUpdate, handleDurationChange, muted]);

  // Handles the clicking Play/Pause for the video player
  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) {
      const video = document.getElementById(videoId) as HTMLVideoElement | null;
      if (video) {
        videoRef.current = video;
        if (video.paused) {
          video
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        } else {
          video.pause();
          setIsPlaying(false);
        }
      }
      return;
    }

    if (videoRef.current.paused) {
      videoRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [videoId]);

  // Handles the seeking of the video player
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  // Handles the start of the seeking of the video player
  const handleSeekStart = () => {
    setIsDragging(true);
  };

  // Handles the end of the seeking of the video player
  const handleSeekEnd = () => {
    setIsDragging(false);
  };

  // Handles the volume change of the video player
  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value);
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = newVolume === 0;
        setVolume(newVolume);
        if (newVolume > 0) {
          setPrevVolume(newVolume);
        }
      }
    },
    []
  );

  // Handles the muting of the video player
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;

    if (videoRef.current.volume > 0) {
      setPrevVolume(videoRef.current.volume);
      videoRef.current.volume = 0;
      videoRef.current.muted = true;
      setVolume(0);
    } else {
      videoRef.current.volume = prevVolume;
      videoRef.current.muted = false;
      setVolume(prevVolume);
    }
  }, [prevVolume]);

  // Handles the toggling of the fullscreen of the video player
  const toggleFullscreen = useCallback(() => {
    if (!videoRef.current) return;

    if (!document.fullscreenElement) {
      videoRef.current
        .requestFullscreen()
        .then(() => {
          if (videoRef.current?.paused) {
            videoRef.current
              .play()
              .then(() => setIsPlaying(true))
              .catch(() => setIsPlaying(false));
          }
        })
        .catch((err) => {
          console.error(
            `Error attempting to enable fullscreen: ${err.message}`
          );
        });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // If the video is not ready, return null
  if (!isReady) {
    return (
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <LoadingSpinner />
      </div>
    );
  }

  // If there is an error, return null and display the error message on server only
  if (error) {
    return (
      <div className="sr-only">
        Error: {error.message}
        <br />
        Video cannot be played.
      </div>
    );
  }

  // If there is no error, return the video player overlay
  return (
    <div className="group absolute z-10 h-full w-full">
      {/* Play/Pause button in middle of the video */}
      <button
        type="button"
        onClick={handlePlayPause}
        className={clsx(
          'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-md bg-secondary-light/50 px-6 py-4 hover:scale-110',
          isPlaying && 'opacity-0 group-hover:opacity-100'
        )}
      >
        {isPlaying ? (
          <PauseIcon className="h-8 w-8 text-white" />
        ) : (
          <PlayIcon className="h-8 w-8 text-white" />
        )}
      </button>
      {/* Footer with play/pause, time, volume, and fullscreen buttons */}
      <footer
        className={clsx(
          'absolute bottom-0 left-0 right-0 z-10 flex h-12 items-center bg-secondary-light/50 px-2 text-white transition-opacity',
          isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          className={clsx('hover:bg-white/20', isMenuOpen && 'mr-2')}
        >
          {isPlaying ? (
            <PauseIcon className="h-5 w-5 text-white" />
          ) : (
            <PlayIcon className="h-5 w-5 text-white" />
          )}
        </button>
        <div
          className={clsx(
            isMenuOpen && 'hidden',
            'flex flex-1 items-center gap-4'
          )}
        >
          <span className="w-12 self-start text-right text-sm tabular-nums text-white">
            {_formatTime(currentTime)}
          </span>
          <input
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            onMouseDown={handleSeekStart}
            onMouseUp={handleSeekEnd}
            onTouchStart={handleSeekStart}
            onTouchEnd={handleSeekEnd}
            className="bg-white/30 h-1 flex-1 appearance-none self-center rounded-lg accent-white hover:h-2"
          />
          <span className="w-12 text-left text-sm tabular-nums text-white">
            {duration ? _formatTime(duration) : ''}
          </span>
        </div>
        <div
          className={clsx(
            menuClass.base,
            isMenuOpen && 'flex-1 justify-between'
          )}
        >
          <div
            className={clsx(
              menuClass.menu,
              isMenuOpen ? menuClass.mobileMenuOpen : menuClass.mobileMenuClosed
            )}
          >
            <div className={clsx(menuClass.volume)}>
              <button
                type="button"
                onClick={toggleMute}
                className="hover:bg-white/20"
              >
                {volume === 0 ? (
                  <SpeakerXMarkIcon className="h-5 w-5 text-white" />
                ) : (
                  <SpeakerWaveIcon className="h-5 w-5 text-white" />
                )}
              </button>
              <div className="invisible absolute -top-40 left-1/2 z-10 -translate-x-1/2 opacity-0 transition-all duration-300 group-hover/volume:visible group-hover/volume:opacity-100 md:left-1/2 md:-translate-x-1/2">
                <div className="flex h-24 flex-col items-center justify-center">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    className="bg-white/30 h-1 w-24 appearance-none rounded-lg accent-white hover:h-2 active:h-2"
                    style={{
                      transform:
                        'rotate(-90deg) translateX(-50%) translateY(-50%)',
                    }}
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleFullscreen}
              className={clsx(menuClass.fullscreen)}
            >
              <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="hover:bg-white/20 relative p-1 md:hidden"
          >
            <EllipsisVerticalIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      </footer>
    </div>
  );
};

const LoadingSpinner = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="64"
      height="64"
      viewBox="0 0 24 24"
      className="animate-spin text-secondary-light"
      aria-label="Loading..."
    >
      <defs>
        <linearGradient
          id="mingcuteLoadingFill0"
          x1="50%"
          x2="50%"
          y1="5.271%"
          y2="91.793%"
        >
          <stop offset="0%" stopColor="currentColor" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.55" />
        </linearGradient>
        <linearGradient
          id="mingcuteLoadingFill1"
          x1="50%"
          x2="50%"
          y1="15.24%"
          y2="87.15%"
        >
          <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      <g fill="none">
        <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
        <path
          fill="url(#mingcuteLoadingFill0)"
          d="M8.749.021a1.5 1.5 0 0 1 .497 2.958A7.5 7.5 0 0 0 3 10.375a7.5 7.5 0 0 0 7.5 7.5v3c-5.799 0-10.5-4.7-10.5-10.5C0 5.23 3.726.865 8.749.021"
          transform="translate(1.5 1.625)"
        />
        <path
          fill="url(#mingcuteLoadingFill1)"
          d="M15.392 2.673a1.5 1.5 0 0 1 2.119-.115A10.48 10.48 0 0 1 21 10.375c0 5.8-4.701 10.5-10.5 10.5v-3a7.5 7.5 0 0 0 5.007-13.084a1.5 1.5 0 0 1-.115-2.118"
          transform="translate(1.5 1.625)"
        />
      </g>
    </svg>
  );
};

/**
 * @description Menu classNames for the video player overlay
 */
const menuClass = {
  base: 'group/menu flex flex-row items-center',
  volume: 'group/volume relative z-10',
  fullscreen: 'group/fullscreen hover:bg-white/20',
  menu: 'group-hover/menu:scale-105',
  mobileMenuClosed: 'hidden md:flex md:flex-row md:items-center',
  mobileMenuOpen: 'flex flex-row items-center gap-2 md:hidden',
};

/**
 * @description Format time in MM:SS
 * @param timeInSeconds - The time in seconds
 * @returns The formatted time
 */
const _formatTime = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};
